import { createContext, useContext } from 'react';
import { LanguageOption } from '@hssoft/auth-web-shared-lib';

import { C_Language } from '../../graphql/generatedModel';
import { storageHelper } from '../helpers/storage';

// Languages offered on the sign-in screen. The locale codes match the app's own
// i18n (de/en/fr/it); the auth library normalizes them internally.
export const AUTH_LANGUAGES: LanguageOption[] = [
  { locale: 'de', label: 'Deutsch' },
  { locale: 'en', label: 'English' },
  { locale: 'fr', label: 'Français' },
  { locale: 'it', label: 'Italiano' },
];

// Sign-in screen title per language — the library only ships a generic default.
export const SIGN_IN_TITLE_BY_LOCALE: Record<string, string> = {
  de: 'Willkommen bei WaWiAssist',
  en: 'Welcome to WaWiAssist',
  fr: 'Bienvenue sur WaWiAssist',
  it: 'Benvenuti su WaWiAssist',
};

export const LOCALE_TO_C_LANGUAGE: Record<string, C_Language> = {
  en: C_Language.L1_ENGLISH,
  de: C_Language.L2_GERMAN,
  fr: C_Language.L3_FRENCH,
  it: C_Language.L4_ITALIAN,
};

const C_LANGUAGE_TO_LOCALE: Record<string, string> = {
  [C_Language.L1_ENGLISH]: 'en',
  [C_Language.L2_GERMAN]: 'de',
  [C_Language.L3_FRENCH]: 'fr',
  [C_Language.L4_ITALIAN]: 'it',
};

// Start the sign-in screen in the language the user last used in the app.
export function readStoredLocale(): string {
  const stored = storageHelper.local.getItem('sys_data.language') as C_Language | null;
  return (stored && C_LANGUAGE_TO_LOCALE[stored]) || 'de';
}

export interface AuthLanguageContextValue {
  selectedLanguage: string;
  onLanguageChange: (locale: string) => void;
}

export const AuthLanguageContext = createContext<AuthLanguageContextValue | null>(null);

export function useAuthLanguage(): AuthLanguageContextValue {
  const ctx = useContext(AuthLanguageContext);
  if (!ctx) {
    throw new Error('useAuthLanguage must be used within <AuthShell>');
  }
  return ctx;
}
